import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
    AppBar,
    Box,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    FormControl,
    FormHelperText,
    Grid,
    MenuItem,
    Select,
    Stack,
    TextField,
    Toolbar,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { REQUIRED_RED } from '../../App';
import { preventPhoneInputKeys } from '../../components/FormatNumber/FormatNumber';
import { getCountryMasterListApi } from '../../redux/Account/peronalIfno/action';
import { createSupplierApi } from '../../redux/Supplier/action';
import { PHONE_TYPES } from '../../utility/Constant';
import { filterUndefined } from '../../utility/helpers/commonFunction';
import { cleanPhoneNumber, inputFormatPhoneNumber, removeDashFromPhoneNumber } from '../Common/FormatPhoneNumber';

const modalStyle = {
    position: 'absolute',
    top: '0%',
    left: '0%',
    width: 400,
    transform: 'translate(-0%, -50%)',
    bgcolor: 'background.paper',
    boxShadow: 24,
};

const AddQuickSupplierModal = ({ openModal, supplierName, onClose, onSave }) => {
    const dispatch = useDispatch();
    const {
        handleSubmit,
        setValue,
        control,
        reset,
        clearErrors,
        setError,
        getValues,
        formState: { errors },
    } = useForm({
        defaultValues: {
            phoneNumberType: {
                key: 'mobile',
                value: 'Mobile',
            },
            name: supplierName,
        },
    });
    const [isLoading, setIsLoading] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedPhoneTypes, setSelectedPhoneTypes] = useState({
        primary: 'mobile',
        secondary: 'mobile',
    });
    const countryMasterList = useSelector((state) => state.account?.countryMasterList);
    // Memoize the function with useCallback to prevent infinite loops
    const fetchCountryMasterList = useCallback(async () => {
        const resp = await dispatch(getCountryMasterListApi());
        if (resp.length > 0) {
            const selected = resp.find((item) => item.default === true);
            setSelectedCountry(selected);
        }
    }, [dispatch]); // Only depends on dispatch

    useEffect(() => {
        if (openModal) {
            setValue('name', supplierName);
        }

        // Fixed the condition - the previous was (!countryMasterList?.length > 0) which is not correct
        if (!countryMasterList || countryMasterList.length === 0) {
            fetchCountryMasterList();
        } else if (countryMasterList.length > 0) {
            const selected = countryMasterList.find((item) => item.default === true);
            setSelectedCountry(selected);
        }
    }, [openModal, countryMasterList, fetchCountryMasterList, setValue, supplierName]);

    const validatePhoneNumber = (number) => {
        const numericValue = number.replace(/\D/g, '');
        return numericValue.length === 10;
    };
    const onSubmit = async (data) => {
        setIsLoading(true);
        const payload = { ...data };
        // Phone number handling
        if (data.phoneNumber) {
            payload.phoneNumberType = payload?.phoneNumberType?.key;
            payload.phoneNumberCountryId = selectedCountry?._id;
            payload.phoneNumber = cleanPhoneNumber(payload.phoneNumber);
        } else {
            delete payload.phoneNumberType;
            delete payload.phoneNumberCountryId;
            delete payload.phoneNumber;
        }

        if (!payload.account || payload === '') {
            payload.account = undefined;
        }

        const resp = await dispatch(createSupplierApi(filterUndefined(payload)));
        if (resp) {
            reset();
            onSave(resp);
        }
        setIsLoading(false);
        // onSave();
    };

    const modalClose = () => {
        reset(); // Reset form values when modal is closed
        onClose();
    };

    //Country Selection fucntion
    const handleCountryChange = (event) => {
        const selected = countryMasterList.find((item) => item._id === event.target.value);
        setSelectedCountry(selected);
    };
    return (
        <Dialog
            open={openModal}
            onClose={() => {
                if (!isLoading) {
                    modalClose();
                }
            }}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className="dialogWithFeild w-720"
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <Box
                    sx={{
                        ...modalStyle,
                        width: '100%',
                        border: 'none',
                        borderRadius: '8px 8px 0px 0px',
                        padding: 0,
                        boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.12), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 2px 1px -1px rgba(0, 0, 0, 0.20)',
                    }}
                >
                    <AppBar
                        color=""
                        variant="outlined"
                        style={{
                            width: 'inherit',
                            borderRadius: '8px 8px 0px 0px !important',
                            padding: '0',
                        }}
                    >
                        <Toolbar variant="regular">
                            <Typography variant="h6" width={'98%'}>
                                Add New Supplier
                            </Typography>
                            <Stack direction={'row'} spacing={0}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (!isLoading) {
                                            modalClose();
                                        }
                                    }}
                                    style={{
                                        cursor: 'pointer',
                                        height: '35px',
                                        background: 'none',
                                        border: 'none',
                                    }}
                                >
                                    <CloseIcon sx={{ fill: 'black', fontSize: 35 }} />
                                </button>
                            </Stack>
                        </Toolbar>
                    </AppBar>
                </Box>
                <Container
                    style={{
                        marginTop: '64px',
                        padding: '0',
                    }}
                >
                    <DialogContent>
                        <Grid container spacing={3}>
                            <Grid item xs={6}>
                                <Stack spacing={1}>
                                    <Typography
                                        variant="body1"
                                        className="label-500"
                                        style={{
                                            letterSpacing: '0.15px',
                                        }}
                                    >
                                        Supplier Name <span style={{ color: REQUIRED_RED }}>*</span>
                                    </Typography>

                                    <Controller
                                        name="name"
                                        control={control}
                                        rules={{
                                            required: 'Supplier name is required',
                                        }}
                                        render={({ field }) => (
                                            <TextField
                                                id="outlined-basic"
                                                variant="outlined"
                                                fullWidth
                                                error={!!errors.name}
                                                helperText={errors.name ? errors.name.message : ''}
                                                {...field}
                                            />
                                        )}
                                    />
                                </Stack>
                            </Grid>

                            <Grid item xs={6}>
                                <Stack spacing={1}>
                                    <Typography variant="body1" className="label-500">
                                        Account #
                                    </Typography>

                                    <Controller
                                        name="account"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                id="outlined-basic"
                                                variant="outlined"
                                                fullWidth
                                                error={!!errors.account}
                                                helperText={errors.account ? errors.account.message : ''}
                                                {...field}
                                            />
                                        )}
                                    />
                                </Stack>
                            </Grid>

                            <Grid item xs={12}>
                                <Stack spacing={1}>
                                    <Typography variant="body1" className="label-500">
                                        {' '}
                                        Primary Phone Number
                                        {/* <span style={{ color: REQUIRED_RED }}>*</span> */}
                                    </Typography>
                                    <div style={{ display: 'flex' }}>
                                        <Grid container spacing={2} rowGap={2}>
                                            <Grid item xs={12} sm={4} md={2.5}>
                                                <FormControl fullWidth error={!!errors.primaryPhoneNumberType}>
                                                    <Controller
                                                        name="phoneNumberType"
                                                        control={control}
                                                        rules={{
                                                            required: 'Phone Number Type is required',
                                                        }}
                                                        render={({ field }) => (
                                                            <Select
                                                                {...field}
                                                                value={
                                                                    selectedPhoneTypes?.primary || '' // Current selected value
                                                                }
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        InputProps={{
                                                                            ...params.InputProps,
                                                                            endAdornment: (
                                                                                <svg
                                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                                    width="25"
                                                                                    height="25"
                                                                                    viewBox="0 0 25 25"
                                                                                    fill="none"
                                                                                >
                                                                                    <title>endAdornment svg</title>
                                                                                    <path
                                                                                        d="M17.09 8.79492L12.5 13.3749L7.91 8.79492L6.5 10.2049L12.5 16.2049L18.5 10.2049L17.09 8.79492Z"
                                                                                        fill="black"
                                                                                        fillOpacity="0.6"
                                                                                    />
                                                                                </svg>
                                                                            ),
                                                                        }}
                                                                    />
                                                                )}
                                                                onChange={(e) => {
                                                                    const selectedValue = e.target.value;
                                                                    setSelectedPhoneTypes({
                                                                        ...selectedPhoneTypes,
                                                                        primary: selectedValue,
                                                                    });
                                                                    field.onChange(selectedValue); // Update form value
                                                                }}
                                                            >
                                                                {PHONE_TYPES?.map((option) => (
                                                                    <MenuItem key={option.key} value={option.key}>
                                                                        {option.value}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        )}
                                                    />
                                                    {errors.phoneNumberType && <FormHelperText>{errors.phoneNumberType.message}</FormHelperText>}
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12} sm={8} md={9.5}>
                                                <Controller
                                                    name="phoneNumber"
                                                    control={control}
                                                    // rules={{
                                                    //     required: 'Phone Number is required',
                                                    // }}
                                                    render={({ field }) => (
                                                        <TextField
                                                            autoComplete="off"
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <Select
                                                                        style={{
                                                                            width: '92px',
                                                                            minWidth: '92px',
                                                                            height: '56px',
                                                                            border: 'none',
                                                                            marginRight: '16.5px',
                                                                        }}
                                                                        MenuProps={{
                                                                            PaperProps: {
                                                                                style: {
                                                                                    width: '264px',
                                                                                    marginLeft: '86px',
                                                                                    boxShadow:
                                                                                        'box-shadow: 0px 3px 14px 2px rgba(0, 0, 0, 0.12), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 5px 5px -3px rgba(0, 0, 0, 0.20)',
                                                                                },
                                                                            },
                                                                            MenuListProps: {
                                                                                style: {},
                                                                            },
                                                                        }}
                                                                        sx={{
                                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                                border: 'none',
                                                                            },
                                                                            '& .MuiOutlinedInput-root': {
                                                                                borderTopRightRadius: '0px',
                                                                                borderBottomRightRadius: '0px',
                                                                            },
                                                                            borderTopRightRadius: '0px',
                                                                            borderBottomRightRadius: '0px',
                                                                            background: '#F1F1F1',
                                                                        }}
                                                                        value={selectedCountry?._id ?? ''}
                                                                        onChange={handleCountryChange}
                                                                        renderValue={(selected) => {
                                                                            const country =
                                                                                countryMasterList.find((item) => item._id === selected) || selectedCountry;
                                                                            if (!country) return 'Select';
                                                                            return (
                                                                                <div
                                                                                    style={{
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                    }}
                                                                                >
                                                                                    <span style={{ marginRight: '8px' }}>{country.flag}</span>
                                                                                    {country.dial_code}
                                                                                </div>
                                                                            );
                                                                        }}
                                                                        IconComponent={KeyboardArrowDownIcon} // Use custom icon
                                                                    >
                                                                        {countryMasterList?.length > 0 &&
                                                                            countryMasterList.map((country) => (
                                                                                <MenuItem key={country._id} value={country._id} style={{ height: '44px' }}>
                                                                                    <span style={{ marginRight: '8px' }}>{country.flag}</span>
                                                                                    {country.country} ({country.dial_code})
                                                                                </MenuItem>
                                                                            ))}
                                                                    </Select>
                                                                ),
                                                            }}
                                                            id="outlined-basic"
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    paddingLeft: '0',
                                                                },
                                                            }}
                                                            variant="outlined"
                                                            fullWidth
                                                            error={!!errors.phoneNumber}
                                                            helperText={errors.phoneNumber ? errors.phoneNumber.message : ''}
                                                            {...field}
                                                            onChange={(e) => {
                                                                if (removeDashFromPhoneNumber(e.target.value).length <= 10) {
                                                                    const input = e.target;
                                                                    const rawValue = e.target.value;
                                                                    // Store previous cursor position
                                                                    const prevCursorPosition = input.selectionStart;
                                                                    const isDeleting = e.nativeEvent.inputType === 'deleteContentBackward';
                                                                    // Count hyphens before formatting
                                                                    const hyphenCountBefore = (rawValue.match(/-/g) || []).length;
                                                                    // Format the input value
                                                                    const formattedValue = inputFormatPhoneNumber(rawValue);

                                                                    // Count hyphens after formatting
                                                                    const hyphenCountAfter = (formattedValue.match(/-/g) || []).length;

                                                                    let newCursorPosition = prevCursorPosition;

                                                                    if (isDeleting) {
                                                                        // If a hyphen was removed, move cursor back by 1
                                                                        if (hyphenCountAfter < hyphenCountBefore) {
                                                                            newCursorPosition--;
                                                                        }
                                                                    } else {
                                                                        // If a hyphen was added, move cursor forward by 1
                                                                        if (hyphenCountAfter > hyphenCountBefore) {
                                                                            newCursorPosition++;
                                                                        }
                                                                    }
                                                                    // Ensure cursor position doesn't go out of bounds
                                                                    newCursorPosition = Math.max(0, Math.min(newCursorPosition, formattedValue.length));
                                                                    // Update the state
                                                                    setValue('phoneNumber', formattedValue);

                                                                    // Restore the adjusted cursor position
                                                                    setTimeout(() => {
                                                                        input.setSelectionRange(newCursorPosition, newCursorPosition);
                                                                    }, 0);
                                                                    const nondashedPhoneNumber = e.target.value.toString()?.replace(/-/g, '');
                                                                    if (
                                                                        nondashedPhoneNumber.length === 10 &&
                                                                        validatePhoneNumber(inputFormatPhoneNumber(e.target.value))
                                                                    ) {
                                                                    }

                                                                    if (removeDashFromPhoneNumber(e.target.value).length < 10) {
                                                                        setError('phoneNumber', {
                                                                            message: 'Phone Number should be 10 digits',
                                                                        });
                                                                    } else clearErrors('phoneNumber');
                                                                }
                                                            }}
                                                            onKeyDown={(e) => {
                                                                preventPhoneInputKeys(e);
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                        </Grid>
                                    </div>
                                </Stack>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ mb: '16px', padding: '0 16px 0 0' }}>
                        <Button
                            onClick={() => {
                                if (!isLoading) {
                                    modalClose();
                                }
                            }}
                            variant="outlined"
                            sx={{ fontSize: '15px' }}
                        >
                            CANCEL
                        </Button>
                        <Button disabled={isLoading} type="submit" variant="contained" sx={{ fontSize: '15px' }}>
                            Confirm
                        </Button>
                    </DialogActions>
                </Container>
            </form>
        </Dialog>
    );
};
export default AddQuickSupplierModal;
